import { createSelector } from "reselect";
import {
  getVariantResult,
  getACMGAnnotationTranscript,
} from "../general.selectors";
import SpliceAiEvent from "../../../models/SpliceAiEvent";

const getSpliceKNCData = createSelector(getVariantResult, (res) => {
  return res?.knc_splice_vault?.[0] ?? { items: [], version: "" };
});

const getSpliceVersion = createSelector(getSpliceKNCData, (res) => {
  return res?.version ?? "";
});

const getFlatSpliceEvents = createSelector(getSpliceKNCData, (res) => {
  if (!res) return [];

  return []
    .concat(...(res?.items ?? [])) // flatten
    .filter((x) => x?.tx_id) // remove items without transcripts
    .sort((a, b) => a.event_rank - b.event_rank); // sort by event_rank
});

const getHasSpliceAiData = createSelector(getFlatSpliceEvents, (items) => {
  return items?.length > 0;
});

const getSpliceTranscripts = createSelector(getFlatSpliceEvents, (events) => {
  const allTranscripts = events.map((item) => item.tx_id);
  return [...new Set(allTranscripts)];
});

const getSplicePerTranscript = createSelector(getFlatSpliceEvents, (events) => {
  // Group events by transcript
  const groupedEvents = events.reduce((acc, event) => {
    acc[event.tx_id] = acc[event.tx_id] ?? [];
    acc[event.tx_id].push(event);
    return acc;
  }, {});

  const entries = Object.entries(groupedEvents)
    .filter(([t, v]) => t && v.length > 0)
    .map(([transcript, spliceEvents]) => {
      return [
        transcript,
        {
          transcript,
          exonNo: spliceEvents[0].exon_no,
          type: spliceEvents[0].ss_type,
          events: spliceEvents.map((x) => new SpliceAiEvent(x)),
        },
      ];
    });

  return Object.fromEntries(entries);
});

const getTranscriptOptions = createSelector(
  getSpliceTranscripts,
  (spliceTranscripts) => {
    return spliceTranscripts.map((x) => ({ label: x, value: x }));
  }
);

const getSelectedTranscript = createSelector(
  getACMGAnnotationTranscript,
  getTranscriptOptions,
  (acmgTranscript, spliceTranscripts) => {
    const acmgStr = typeof acmgTranscript === "string" ? acmgTranscript : "";
    const acmgVal = acmgStr ? acmgStr.split(".")[0] : ""; // remove transcript version
    const acmgTr = spliceTranscripts.find((x) => acmgVal === x.value);
    return acmgTr ?? spliceTranscripts[0];
  }
);

export {
  getHasSpliceAiData,
  getSpliceKNCData,
  getSpliceVersion,
  getSpliceTranscripts,
  getSplicePerTranscript,
  getTranscriptOptions,
  getSelectedTranscript,
};
